import { falsey } from '@/helpers';
import { DateTime } from 'luxon';
import { Model } from 'pinia-orm';
import { Attr, BelongsTo, Bool, HasMany, HasManyThrough, HasOne, Num, Str } from 'pinia-orm/dist/decorators';
import DayworksCost from './DayworksCost';
import LumpSumCost from './LumpSumCost';
import PackageClaim from './PackageClaim';
import Project from './Project';
import RatesCost from './RatesCost';
import ScopeGap from './ScopeGap';
import SharedFundCost from './SharedFundCost';
import Trade from './Trade';
import TradeItemCost from './TradeItemCost';
import VariationOrder from './VariationOrder';
import VariationOrderCost from './VariationOrderCost';
/* --- user header --- */
/* --- end user header --- */

export const ClaimStatus = {
  draft: 0,
  draft_pqs: 10,
  draft_subcontractor: 11,
  draft_contractor: 20,
  contractor_claim: 30,
  subcontractor_claim: 31,
  pqs_review: 40,
  certified: 50,
} as const;

export const ClaimStatusTitles = {
  draft: 'Draft',
  draft_pqs: 'Draft Issued to PQS',
  draft_subcontractor: 'Issued',
  draft_contractor: 'Draft Issued to Contractor',
  contractor_claim: 'Draft Issued to Contractor',
  subcontractor_claim: 'Claim Submitted',
  pqs_review: 'Claim Submitted',
  certified: 'Certified',
};

export const ClaimTypeTitles = {
  direct: 'Construction',
  indirect: 'Development',
  subcontractor_lump_sum: 'Lump Sum',
  subcontractor_schedule_of_rates: 'Schedule of Rates',
} as const;

export default class Claim extends Model {
  static entity = 'App\\Models\\Claim';
  // fields
  @Num(0)
  declare id: number;
  @Num(0)
  declare project_id: number;
  @Str('')
  declare start: string;
  @Str('')
  declare end: string;
  @Num(0)
  declare retention: number;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  @Num(0)
  declare claim_number: number;
  @Bool(true)
  declare capture_claim_amount: boolean;
  @Num(0)
  declare status: ObjectValues<typeof ClaimStatus>;
  @Str('')
  declare submitted_at_time_zone: string | undefined;
  @Str('')
  declare submitted_at: string | undefined;
  @Str('')
  declare draft_issued_at_time_zone: string | undefined;
  @Str('')
  declare draft_issued_at: string | undefined;
  @Str('')
  declare claim_issued_at_time_zone: string | undefined;
  @Str('')
  declare claim_issued_at: string | undefined;
  @Str('')
  declare certificate_issued_at_time_zone: string | undefined;
  @Str('')
  declare certificate_issued_at: string | undefined;
  @Str('')
  declare boq_type: string | undefined;
  @Bool(undefined)
  declare stat_dec_received: boolean | undefined;
  @Bool(false)
  declare sopa_disclaimer: boolean;
  // relations
  @BelongsTo(() => Project, 'project_id', 'id')
  declare project: Project;
  @HasManyThrough(() => Trade, () => Project, 'id', 'project_id', 'project_id', 'id')
  declare trades: Trade[];
  @HasManyThrough(() => VariationOrder, () => Project, 'id', 'project_id', 'project_id', 'id')
  declare variation_orders: VariationOrder[];
  @HasMany(() => RatesCost, 'claim_id', 'id')
  declare rates_costs: RatesCost[];
  @HasMany(() => LumpSumCost, 'claim_id', 'id')
  declare lump_sum_costs: LumpSumCost[];
  @HasMany(() => TradeItemCost, 'claim_id', 'id')
  declare trade_item_costs: TradeItemCost[];
  @HasMany(() => VariationOrderCost, 'claim_id', 'id')
  declare variation_order_costs: VariationOrderCost[];
  @HasMany(() => DayworksCost, 'claim_id', 'id')
  declare dayworks_costs: DayworksCost[];
  @HasMany(() => SharedFundCost, 'claim_id', 'id')
  declare shared_fund_costs: SharedFundCost[];
  @HasOne(() => PackageClaim, 'claim_id', 'id')
  declare package_claim: PackageClaim;
  @HasOne(() => ScopeGap, 'claim_id', 'id')
  declare scope_gap: ScopeGap;

  /* --- user code --- */

  // Computed Properties
  @Attr(undefined)
  declare $total_claim: number | undefined;
  @Attr(undefined)
  declare $total_certified: number | undefined;
  @Attr(undefined)
  declare $total_budget: number | undefined;
  @Attr(undefined)
  declare $total_previous: number | undefined;
  @Attr(undefined)
  declare $total_previous_claim: number | undefined;
  @Attr(undefined)
  declare $total_actual_forecast: number | undefined;

  get totalClaim(): number {
    if(!falsey(this.$total_claim)) {
      return this.$total_claim;
    }

    return (this.trade_item_costs?.reduce((total, value) => total + value.current_claim, 0) || 0) +
      (this.variation_order_costs?.reduce((total, value) => total + value.current_claim, 0) || 0) +
      (this.shared_fund_costs?.reduce((total, value) => total + (value.current_claim || 0), 0) || 0) +
      (this.dayworks_costs?.reduce((total, value) => total + (value.current_claim || 0), 0) || 0);
  }

  get totalCertified(): number {
    if(!falsey(this.$total_certified)) {
      return this.$total_certified;
    }

    if(this.boq_type === 'subcontractor_lump_sum') {
      return (this.lump_sum_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) +
        (this.variation_order_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) +
        (this.dayworks_costs?.reduce((total, value) => total + (value.current_certified || 0), 0) || 0);
    }

    if(this.boq_type === 'subcontractor_schedule_of_rates') {
      return (this.rates_costs?.reduce((total, value) => {
        return total += value.current_certified * value.package_trade_item.rate;
      }, 0) || 0) + (this.variation_order_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) +
        (this.dayworks_costs?.reduce((total, value) => total + (value.current_certified || 0), 0) || 0);
    }

    return (this.trade_item_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) +
      (this.variation_order_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) +
      (this.shared_fund_costs?.reduce((total, value) => total + value.current_certified, 0) || 0);
  }

  get totalBudget(): number {
    if(!falsey(this.$total_budget)) {
      return this.$total_budget;
    }

    return this.trade_item_costs?.reduce((total, value) => total + value.current_certified, 0) +
        (this.variation_order_costs?.reduce((total, value) => total + value.current_certified, 0) || 0) ||
      0;
  }

  get totalPrevious(): number {
    return this.$total_previous ?? 0;
  }

  get totalPreviousClaim(): number {
    return this.$total_previous_claim ?? 0;
  }

  get totalActualForecast(): number {
    return this.$total_actual_forecast ?? 0;
  }

  get totalVariance(): number {
    return this.totalClaim - this.totalCertified;
  }

  get cumulativeClaim(): number {
    return this.totalPreviousClaim + this.totalClaim;
  }

  get cumulativeCertified(): number {
    return this.totalPrevious + this.totalCertified;
  }

  get cumulativeVariance(): number {
    return this.cumulativeClaim - this.cumulativeCertified;
  }

  get totalPayable(): number {
    return this.totalCertified - this.retention;
  }

  get draftIssuedAt() {
    return this.draft_issued_at ?
      DateTime.fromISO(this.draft_issued_at).setZone(this.draft_issued_at_time_zone) :
      undefined;
  }

  get claimIssuedAt() {
    return this.claim_issued_at ?
      DateTime.fromISO(this.claim_issued_at).setZone(this.claim_issued_at_time_zone) :
      undefined;
  }

  get certificateIssuedAt() {
    return this.certificate_issued_at ?
      DateTime.fromISO(this.certificate_issued_at).setZone(this.certificate_issued_at_time_zone) :
      undefined;
  }

  get submittedAt() {
    return this.submitted_at ? DateTime.fromISO(this.submitted_at).setZone(this.submitted_at_time_zone) : undefined;
  }

  get createdAt() {
    return this.created_at ? DateTime.fromISO(this.created_at) : undefined;
  }

  get startDate() {
    return this.start ? DateTime.fromISO(this.start) : undefined;
  }

  get endDate() {
    return this.end ? DateTime.fromISO(this.end) : undefined;
  }
  /* --- end user code --- */
}
